/**
 * Evaluation Query Hooks
 *
 * Dedicated React Query hooks for the /evaluation* routes:
 *   - /evaluation              (EvaluationDashboard)
 *   - /evaluation/manage       (EvaluationManagement)
 *
 * Mirrors `useStrategyQuery.ts`: per-pillar queries with stable keys under
 * the `['evaluation', ...]` family, mutations that invalidate the right
 * subtree, and `placeholderData: keepPreviousData` so the table/grid don't
 * blank on filter changes.
 */

import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import {
  cyclesApi,
  frameworksApi,
  evaluationDashboardApi,
} from '../../services/evaluationApi';
import type {
  BlendComparisonParams,
  BlendComparisonResponse,
} from '../../services/evaluationApi';
import { queryKeys, STALE_TIMES } from '../../providers/QueryProvider';
import type {
  EvaluationCycle,
  EvaluationFramework,
  EvaluationFrequency,
  DashboardEntity,
} from '../../types/evaluation';

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

export interface DashboardFilters {
  cycleId?: number | string;
  period?: string;
  year?: number | string;
  status?: string;
  frequency?: EvaluationFrequency;
}

// Strip undefined / empty values so the cache key doesn't fragment on the
// empty-string defaults the screens emit before the user touches a filter.
function normalizeDashboardFilters(filters?: DashboardFilters) {
  if (!filters) return {};
  const out: Record<string, string | number> = {};
  if (filters.cycleId) out.cycle = Number(filters.cycleId);
  if (filters.period) out.period = filters.period;
  if (filters.year) out.year = Number(filters.year);
  if (filters.status) out.status = filters.status;
  if (filters.frequency) out.frequency = filters.frequency;
  return out;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useEvaluationCyclesQuery(year?: number | string | null) {
  return useQuery<EvaluationCycle[]>({
    queryKey: queryKeys.evaluation.cycles({ year: year ?? null }),
    queryFn: async () => {
      const params: Record<string, number | string> = {};
      if (year) params.year = Number(year);
      return await cyclesApi.list(params);
    },
    staleTime: STALE_TIMES.EVALUATION,
    placeholderData: keepPreviousData,
  });
}

export function useEvaluationFrameworksQuery() {
  return useQuery<EvaluationFramework[]>({
    queryKey: queryKeys.evaluation.frameworks(),
    queryFn: () => frameworksApi.list(),
    staleTime: STALE_TIMES.EVALUATION,
    placeholderData: keepPreviousData,
  });
}

export interface EvaluationManagementBundle {
  cycles: EvaluationCycle[];
  frameworks: EvaluationFramework[];
}

/**
 * Bundles the two list calls /evaluation/manage needs into a single query
 * so the screen has one loading state, one error state, and one cache key
 * that the duplicate / delete / launch mutations can invalidate.
 */
export function useEvaluationManagementQuery(frequency?: EvaluationFrequency) {
  return useQuery<EvaluationManagementBundle>({
    queryKey: queryKeys.evaluation.management(frequency),
    queryFn: async () => {
      const params = frequency ? { frequency } : undefined;
      const [cycles, frameworks] = await Promise.all([
        cyclesApi.list(params),
        frameworksApi.list(params),
      ]);
      return { cycles, frameworks };
    },
    staleTime: STALE_TIMES.EVALUATION,
    placeholderData: keepPreviousData,
  });
}

export function useEvaluationDashboardQuery(filters?: DashboardFilters) {
  const params = normalizeDashboardFilters(filters);
  return useQuery<DashboardEntity[]>({
    queryKey: queryKeys.evaluation.dashboard(params),
    queryFn: () => evaluationDashboardApi.list(params),
    staleTime: STALE_TIMES.EVALUATION,
    placeholderData: keepPreviousData,
  });
}

export function useEvaluationComparisonQuery(cycleId: number | null, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.evaluation.comparison(cycleId),
    queryFn: () => evaluationDashboardApi.comparison(cycleId ?? undefined),
    staleTime: STALE_TIMES.EVALUATION,
    enabled,
    placeholderData: keepPreviousData,
  });
}

// Blended per-department score ("مقارنة أداء الجهات وفق التقييم" chart).
export function useEvaluationBlendComparisonQuery(
  params?: BlendComparisonParams,
  enabled: boolean = true,
) {
  const normalized: Record<string, string | number> = {};
  if (params?.cycle) normalized.cycle = params.cycle;
  if (params?.period) normalized.period = params.period;
  if (params?.year) normalized.year = params.year;
  if (params?.to_date) normalized.to_date = params.to_date;
  if (params?.department) normalized.department = params.department;
  return useQuery<BlendComparisonResponse>({
    queryKey: ['evaluation', 'blend-comparison', normalized],
    queryFn: () => evaluationDashboardApi.blendComparison(normalized as BlendComparisonParams),
    staleTime: STALE_TIMES.EVALUATION,
    enabled,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Mutations — each invalidates the keys whose data it could have changed.
// ---------------------------------------------------------------------------

function invalidateEvaluationFamily(queryClient: ReturnType<typeof useQueryClient>) {
  // Single coarse invalidation: the evaluation cluster is small enough that
  // refetching the management bundle + dashboard is cheaper than maintaining
  // a per-mutation precise cascade.
  queryClient.invalidateQueries({ queryKey: queryKeys.evaluation.all });
}

export function useDuplicateCycleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cycleId: number) => cyclesApi.duplicate(cycleId),
    onSuccess: () => invalidateEvaluationFamily(queryClient),
  });
}

export function useDeleteCycleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cycleId: number) => cyclesApi.delete(cycleId),
    onSuccess: () => invalidateEvaluationFamily(queryClient),
  });
}

export function useDeleteFrameworkMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (frameworkId: number) => frameworksApi.delete(frameworkId),
    onSuccess: () => invalidateEvaluationFamily(queryClient),
  });
}

export function useLaunchCycleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (cycleId: number) => cyclesApi.launch(cycleId),
    onSuccess: () => invalidateEvaluationFamily(queryClient),
  });
}

export function useSubmitFrameworkForApprovalMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (frameworkId: number) => frameworksApi.submitForApproval(frameworkId),
    onSuccess: () => invalidateEvaluationFamily(queryClient),
  });
}
